/**
 * Behavior of the bundle configurator (snippets/block-configurator.liquid): total and stock, the
 * confirm dialog before a material is removed, and the color comparison link. The color picker, the
 * quick view and the cart add live in configurator-variant-picker.js, configurator-quick-view.js
 * and bhgl.min.js.
 */

// ---- Total and stock ----

function formatMoney(cents) {
  if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
    return theme.Currency.formatMoney(cents, document.body.dataset.moneyFormat);
  }
  return (cents / 100).toFixed(2);
}

function setupTotal(root) {
  const configEl = root.querySelector('[data-configurator-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  const materialItems = root.querySelectorAll(
    '[data-configurator-materials] [data-configurator-item]',
  );
  const accessoryItems = root.querySelectorAll(
    '[data-configurator-accessories] [data-configurator-item]',
  );
  const selectedSizeEl = root.querySelector('[data-configurator-selected-size]');
  const totalEl = root.querySelector('[data-configurator-total]');
  const warningEl = root.querySelector('[data-configurator-warning]');
  const shortWarningEl = root.querySelector('[data-configurator-short-warning]');
  const addAllEl = root.querySelector('[data-configurator-add-all]');
  const instructionEl = root.querySelector('[data-configurator-instruction]');
  const deliveryEl = root.querySelector('[data-configurator-delivery]');

  function getSelectedSize() {
    const checked = root.querySelector('[data-configurator-size-input]:checked');
    // One size has no list
    return checked ? checked.value : Object.keys(config.sizes)[0] || null;
  }

  // Sold out = this size needs more than data-stock (0 = sold out, missing = not limited).
  // The color picker offers only colors with that many units.
  function syncAvailability(item, qty) {
    const checkbox = item.querySelector('[data-configurator-checkbox]');
    if (!checkbox) return;

    const needed = Math.max(qty, 1);
    const stock = item.getAttribute('data-stock');
    const soldOut = stock !== null && parseInt(stock, 10) < needed;
    const wasSoldOut = item.classList.contains('configurator-card-item--sold-out');
    const picker = item.querySelector('variant-picker');

    item.classList.toggle('configurator-card-item--sold-out', soldOut);
    checkbox.disabled = soldOut;
    if (soldOut) checkbox.checked = false;
    else if (wasSoldOut) checkbox.checked = true;
    if (picker) picker.setAttribute('min-stock', needed);
  }

  function recalculate() {
    const size = getSelectedSize();
    const quantities = (size && config.sizes[size]) || [];
    // The instruction counts while its step-1 box is ticked
    let total = instructionEl && !instructionEl.checked ? 0 : config.basePrice || 0;

    materialItems.forEach((item) => {
      const index = parseInt(item.getAttribute('data-material-index'), 10);
      const unitPrice = parseInt(item.getAttribute('data-unit-price'), 10) || 0;
      const qty = quantities[index] !== undefined ? parseInt(quantities[index], 10) : 1;
      const qtyEl = item.querySelector('[data-configurator-item-qty]');
      const priceEl = item.querySelector('[data-configurator-item-price]');
      const checkbox = item.querySelector('[data-configurator-checkbox]');
      const lineTotal = unitPrice * qty;

      syncAvailability(item, qty);

      // bhgl.min.js posts this as the quantity (jQuery caches it)
      if (checkbox) {
        checkbox.setAttribute('data-multiplicator', qty);
        if (window.jQuery) window.jQuery(checkbox).removeData('multiplicator');
      }

      if (qtyEl) qtyEl.textContent = qty;
      if (priceEl) priceEl.textContent = formatMoney(lineTotal);
      if (!checkbox || checkbox.checked) total += lineTotal;
    });

    accessoryItems.forEach((item) => {
      const unitPrice = parseInt(item.getAttribute('data-unit-price'), 10) || 0;
      const priceEl = item.querySelector('[data-configurator-item-price]');
      const checkbox = item.querySelector('[data-configurator-checkbox]');
      // A picked color can change the price
      if (priceEl) priceEl.textContent = formatMoney(unitPrice);
      if (!checkbox || checkbox.checked) total += unitPrice;
    });

    // Greyed-out materials: sold out, or short for this size
    const greyed = Array.from(
      root.querySelectorAll('[data-configurator-materials] .configurator-card-item--sold-out'),
    );

    if (selectedSizeEl && size) selectedSizeEl.textContent = size;
    if (totalEl) totalEl.textContent = formatMoney(total);
    if (addAllEl) {
      // Nothing ticked = nothing to add; any material greyed out = secondary
      addAllEl.disabled = !root.querySelector('[data-configurator-checkbox]:checked');
      addAllEl.classList.toggle('btn--secondary', greyed.length > 0);
    }
    // data-stock 0 = sold out; a greyed-out card with stock left is short for this size
    if (warningEl) {
      warningEl.hidden = !root.querySelector('[data-configurator-materials] [data-stock="0"]');
    }
    if (shortWarningEl) {
      shortWarningEl.hidden = !greyed.some((item) => item.getAttribute('data-stock') !== '0');
    }
    // Stock status and delivery times only while the instruction and every material can be bought
    if (deliveryEl) {
      deliveryEl.hidden = deliveryEl.hasAttribute('data-instruction-out') || greyed.length > 0;
    }
  }

  root.querySelectorAll('[data-configurator-size-input]').forEach((input) => {
    input.addEventListener('change', recalculate);
  });
  root.querySelectorAll('[data-configurator-checkbox]').forEach((checkbox) => {
    checkbox.addEventListener('change', recalculate);
  });

  // Browsers can restore a previously-checked radio (bfcache/autofill) that no
  // longer matches the server-rendered numbers, so sync once on load too.
  recalculate();
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('block-configurator').forEach(setupTotal);
});

// ---- Color comparison ----
// The "Farben vergleichen" link opens a sheet (in snippets/block-configurator.liquid) with the pictures
// of the ticked cards of its step (the materials and the accessories sorted in next to them). They are
// read at click time, so colors changed with the color picker are included.

// Cards use 144px thumbnails; the tiles need more.
const IMAGE_WIDTH = 480;

function largerImage(src) {
  try {
    const url = new URL(src, window.location.href);
    if (!url.searchParams.has('width')) return src;
    url.searchParams.set('width', String(IMAGE_WIDTH));
    return url.href;
  } catch {
    return src;
  }
}

/** Picture and alt text of each ticked card in the section. */
function chosenPictures(section) {
  return Array.from(section.querySelectorAll('[data-configurator-item]'))
    .filter((card) => {
      const checkbox = card.querySelector('[data-configurator-checkbox]');
      return checkbox && checkbox.checked;
    })
    .map((card) => {
      const image = card.querySelector('[data-configurator-item-image]');
      const title = card.querySelector('.configurator-card-item__title');
      return {
        src: image && image.getAttribute('src') ? largerImage(image.getAttribute('src')) : '',
        alt: (image && image.alt) || (title && title.textContent.trim()) || '',
      };
    });
}

function colorTile({ src, alt }) {
  const image = document.createElement('img');
  image.className = 'configurator-color-compare__image';
  image.src = src;
  image.alt = alt || '';
  image.loading = 'lazy';
  image.decoding = 'async';

  const tile = document.createElement('li');
  tile.className = 'configurator-color-compare__tile';
  tile.append(image);
  return tile;
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-configurator-color-compare-trigger]');
  if (!trigger) return;
  event.preventDefault();

  const root = trigger.closest('block-configurator');
  const dialog = root && root.querySelector('[data-color-comparison-dialog]');
  const section = trigger.closest('.configurator-step');
  if (!dialog || !section || dialog.open) return;

  const tiles = chosenPictures(section)
    .filter((item) => item.src)
    .map(colorTile);
  const grid = dialog.querySelector('[data-color-comparison-grid]');
  const empty = dialog.querySelector('[data-color-comparison-empty]');
  grid.replaceChildren(...tiles);
  grid.hidden = tiles.length === 0;
  empty.hidden = tiles.length > 0;
  dialog.showModal();
});

// ---- Dialogs ----
// Close button and backdrop of the confirm dialog and the sheet. A click on the backdrop targets the
// dialog itself. configurator.css stops the page from scrolling while one is open.

document.addEventListener('click', (event) => {
  const dialog = event.target.closest('dialog.configurator-dialog, dialog.configurator-sheet');
  if (
    dialog &&
    (event.target === dialog || event.target.closest('[data-configurator-dialog-close]'))
  ) {
    dialog.close();
  }
});

// ---- Removed-material confirm ----
// Asks before a material leaves the bundle (dialog markup in snippets/block-configurator.liquid):
// when its checkbox is unticked, and when "Alles in den Warenkorb" runs with one unticked. Each is a
// block setting, passed as data-configurator-ask-on-untick / -ask-on-add on <block-configurator>.
// Sold-out materials are disabled, so they don't count.

const MATERIAL_CHECKBOX = '[data-configurator-materials] [data-configurator-checkbox]';
const UNTICKED_MATERIAL = `${MATERIAL_CHECKBOX}:not(:disabled):not(:checked)`;

// What the open dialog does on "Trotzdem entfernen" (confirm) and "Behalten" (keep)
let pending = null;

function setChecked(checkbox, checked) {
  checkbox.checked = checked;
  // Recalculates the total
  checkbox.dispatchEvent(new Event('change', { bubbles: true }));
}

/** The question this click raises, if any. */
function removal(block, target) {
  const askOnUntick = block.hasAttribute('data-configurator-ask-on-untick');
  const askOnAdd = block.hasAttribute('data-configurator-ask-on-add');

  // The box has already flipped when its click runs, so unticked means it was just ticked
  if (askOnUntick && target.matches(MATERIAL_CHECKBOX) && !target.checked) {
    return { confirm: () => setChecked(target, false) };
  }

  const addButton = target.closest('[data-configurator-add-all]');
  const unticked = block.querySelectorAll(UNTICKED_MATERIAL);
  if (askOnAdd && addButton && unticked.length) {
    return {
      confirm: () => addButton.click(),
      keep: () => unticked.forEach((checkbox) => setChecked(checkbox, true)),
    };
  }
}

// Capturing, because bhgl.min.js binds the add-to-cart click on the button itself
document.addEventListener(
  'click',
  (event) => {
    // The click replayed on "Trotzdem entfernen" is not trusted: let it through
    if (!event.isTrusted) return;

    const { target } = event;
    const block = target.closest('block-configurator');
    const dialog = block && block.querySelector('[data-configurator-remove-dialog]');
    if (!dialog) return;

    if (dialog.contains(target)) return;

    pending = removal(block, target);
    if (!pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    dialog.returnValue = '';
    dialog.showModal();
  },
  true,
);

// close does not bubble, so catch it while capturing. Escape and the backdrop leave returnValue empty.
document.addEventListener(
  'close',
  (event) => {
    if (!event.target.matches('[data-configurator-remove-dialog]')) return;

    const action = pending;
    pending = null;
    if (action) action[event.target.returnValue]?.();
  },
  true,
);
